import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "sonner";
import { PlusCircle } from "lucide-react";

import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import {
  Cassette,
  CreateCustomerDocument,
  Customer,
  DeleteCustomerDocument,
  Eru,
  CustomersDocument,
  type CustomersQueryVariables,
  Organisation,
} from "generated/graphql";
import { Routes } from "~/routes";
import AddCustomerModal, { AddCustomerFormData } from "./AddCustomerModal";
import CustomerAccordion, {
  CustomerAccordionContent,
} from "./CustomerAccordion";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "~/components/ui/pagination";

interface RelationPageInfo {
  hasNextPage: boolean;
  endCursor?: string | null;
}

interface PartialSerialNumberForAccordion {
  serialNumber: number;
  range: {
    productType: {
      code: string;
    };
  };
}

interface PartialCassetteForAccordion extends Pick<Cassette, "id"> {
  remainingCustomerTests?: number | null;
  remainingPhysicalSensors?: number | null;
  hasOpenOverflowWarning?: boolean | null;
  serialNumber?: PartialSerialNumberForAccordion | null;
}

type CustomerWithRelations = Pick<
  Customer,
  | "id"
  | "name"
  | "description"
  | "customerSince"
  | "contactName"
  | "contactEmail"
  | "contactPhone"
  | "notes"
  | "limitIndicators"
> & {
  allowedIndicatorDefinitions?: Array<{
    id: string;
    name: string;
    displayName?: string | null;
  }> | null;
  organisations?: {
    nodes?: Array<Pick<Organisation, "id" | "name" | "logoUrl"> | null> | null;
    pageInfo: RelationPageInfo;
  } | null;
  erus?: {
    nodes?: Array<Pick<Eru, "id" | "name"> | null> | null;
    pageInfo: RelationPageInfo;
  } | null;
  cassettes?: {
    nodes?: Array<PartialCassetteForAccordion | null> | null;
    pageInfo: RelationPageInfo;
  } | null;
};

type CustomerRelationType = "organisations" | "erus" | "cassettes";

type CustomerRelationCountOverrides = Record<
  string,
  Partial<Record<CustomerRelationType, number>>
>;

const Customers: React.FC = () => {
  const [page, setPage] = useState(0);
  const [pageCursors, setPageCursors] = useState<Map<number, string | null>>(
    new Map([[0, null]])
  );
  const [relationCountOverrides, setRelationCountOverrides] =
    useState<CustomerRelationCountOverrides>({});

  const queryVariables: CustomersQueryVariables = useMemo(
    () => ({
      after: pageCursors.get(page) ?? null,
    }),
    [page, pageCursors]
  );

  const { loading, error, data } = useQuery(CustomersDocument, {
    variables: queryVariables,
    onError: (queryError) => {
      toast.error(`Failed to load customers: ${queryError.message}`);
    },
    errorPolicy: "all",
  });
  const [createCustomer] = useMutation(CreateCustomerDocument);
  const [deleteCustomer] = useMutation(DeleteCustomerDocument);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);

  const pageActions = [
    {
      text: "Add Customer",
      action: () => setShowAddCustomerModal(true),
      variant: "primary" as const,
      icon: <PlusCircle className="size-4" aria-hidden="true" />,
    },
  ];

  const handleAddCustomer = async (customerData: AddCustomerFormData) => {
    try {
      await createCustomer({
        variables: {
          input: customerData,
        },
        refetchQueries: [
          {
            query: CustomersDocument,
            variables: queryVariables,
          },
        ],
        awaitRefetchQueries: true,
      });
      setShowAddCustomerModal(false);
      toast.success("Customer added successfully");
    } catch (mutationError) {
      toast.error(
        `Error adding customer: ${
          mutationError instanceof Error
            ? mutationError.message
            : "Unknown error"
        }`
      );
    }
  };

  const handleDeleteCustomer = async (customerId: string) => {
    try {
      await deleteCustomer({
        variables: { id: customerId },
        refetchQueries: [
          {
            query: CustomersDocument,
            variables: queryVariables,
          },
        ],
        awaitRefetchQueries: true,
      });
      toast.success("Customer deleted successfully");
    } catch (mutationError) {
      toast.error(
        `Error deleting customer: ${
          mutationError instanceof Error
            ? mutationError.message
            : "Unknown error"
        }`
      );
    }
  };

  const breadcrumbs = [
    {
      label: "Admin",
      path: Routes.Admin,
    },
    {
      label: "Customers",
      path: Routes.Customers,
    },
  ];

  const customersNodes = data?.customers?.nodes ?? [];
  const hasNextPage = Boolean(data?.customers?.pageInfo?.hasNextPage);
  const nextCursor = data?.customers?.pageInfo?.endCursor ?? null;
  const customers = customersNodes as CustomerWithRelations[];

  useEffect(() => {
    if (!hasNextPage || !nextCursor) {
      return;
    }

    setPageCursors((previous) => {
      if (previous.get(page + 1) === nextCursor) {
        return previous;
      }
      const next = new Map(previous);
      next.set(page + 1, nextCursor);
      return next;
    });
  }, [hasNextPage, nextCursor, page]);

  const handleItemsChange = useCallback(
    (
      customerId: string,
      type: CustomerRelationType,
      items: Array<unknown>
    ) => {
      setRelationCountOverrides((previous) => ({
        ...previous,
        [customerId]: {
          ...previous[customerId],
          [type]: items.length,
        },
      }));
    },
    []
  );

  const accordionItems = useMemo(
    () =>
      customers.map((customer) => ({
        id: customer.id,
        title: customer.name,
        subtitle: customer.description || undefined,
        stats: [
          {
            label: "Organisations",
            value:
              relationCountOverrides[customer.id]?.organisations ??
              customer.organisations?.nodes?.length ??
              0,
            className: "bg-sky-100 text-sky-700",
          },
          {
            label: "ERUs",
            value:
              relationCountOverrides[customer.id]?.erus ??
              customer.erus?.nodes?.length ??
              0,
            className: "bg-violet-100 text-violet-700",
          },
          {
            label: "Cassettes",
            value:
              relationCountOverrides[customer.id]?.cassettes ??
              customer.cassettes?.nodes?.length ??
              0,
            className: "bg-emerald-100 text-emerald-700",
          },
        ],
        children: (
          <CustomerAccordionContent
            key={customer.id}
            customerId={customer.id}
            customerName={customer.name}
            customerSince={customer.customerSince}
            description={customer.description}
            contactName={customer.contactName}
            contactEmail={customer.contactEmail}
            contactPhone={customer.contactPhone}
            notes={customer.notes}
            limitIndicators={customer.limitIndicators ?? false}
            allowedIndicatorDefinitions={
              customer.allowedIndicatorDefinitions ?? []
            }
            organisationsPageInfo={customer.organisations?.pageInfo}
            erusPageInfo={customer.erus?.pageInfo}
            cassettesPageInfo={customer.cassettes?.pageInfo}
            organisations={
              customer.organisations?.nodes?.reduce<
                Array<Pick<Organisation, "id" | "name" | "logoUrl">>
              >((acc, org) => {
                if (org && org.id) {
                  acc.push({
                    id: org.id,
                    name: org.name || "Unnamed Organization",
                    logoUrl: org.logoUrl,
                  });
                }
                return acc;
              }, []) || []
            }
            erus={
              customer.erus?.nodes?.reduce<Array<{ id: string; name: string }>>(
                (acc, eru) => {
                  if (eru && eru.id) {
                    acc.push({
                      id: eru.id,
                      name: eru.name || "",
                    });
                  }
                  return acc;
                },
                []
              ) || []
            }
            cassettes={
              customer.cassettes?.nodes?.reduce<
                Array<{
                  id: string;
                  remainingCustomerTests?: number | null;
                  remainingPhysicalSensors?: number | null;
                  hasOpenOverflowWarning?: boolean | null;
                  serialNumber: {
                    serialNumber: number;
                    range: { productType: { code: string } };
                  } | null;
                }>
              >((acc, cass) => {
                if (cass && cass.id) {
                  acc.push({
                    id: cass.id,
                    remainingCustomerTests: cass.remainingCustomerTests ?? null,
                    remainingPhysicalSensors:
                      cass.remainingPhysicalSensors ?? null,
                    hasOpenOverflowWarning:
                      cass.hasOpenOverflowWarning ?? null,
                    serialNumber: cass.serialNumber?.range?.productType?.code
                      ? {
                          serialNumber: cass.serialNumber.serialNumber,
                          range: {
                            productType: {
                              code: cass.serialNumber.range.productType.code,
                            },
                          },
                        }
                      : null,
                  });
                }
                return acc;
              }, []) || []
            }
            loading={loading}
            onItemsChange={(type, items) =>
              handleItemsChange(customer.id, type, items)
            }
            onTriggerConfirmDelete={() => handleDeleteCustomer(customer.id)}
            deleteConfirmationTitle={`Delete ${customer.name}`}
            deleteConfirmationMessage="Are you sure you want to delete this customer? This action cannot be undone."
          />
        ),
      })),
    [customers, handleItemsChange, loading, relationCountOverrides]
  );

  if (error && !customersNodes.length) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <PageTemplate
      title="Customers"
      breadcrumbs={breadcrumbs}
      actions={pageActions}
      activeItem="Admin"
      activeSubItem="Customers"
    >
      <div className="flex flex-col gap-6">
        <CustomerAccordion items={accordionItems} />
        {(page > 0 || hasNextPage) && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    if (page > 0) {
                      setPage((currentPage) => currentPage - 1);
                    }
                  }}
                  className={
                    page === 0 ? "pointer-events-none opacity-50" : undefined
                  }
                  aria-disabled={page === 0}
                />
              </PaginationItem>
              <PaginationItem>
                <span className="px-3 text-sm text-muted-foreground">
                  Page {page + 1}
                </span>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    if (hasNextPage) {
                      setPage((currentPage) => currentPage + 1);
                    }
                  }}
                  className={
                    !hasNextPage ? "pointer-events-none opacity-50" : undefined
                  }
                  aria-disabled={!hasNextPage}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>

      <AddCustomerModal
        isOpen={showAddCustomerModal}
        onClose={() => setShowAddCustomerModal(false)}
        onSubmit={handleAddCustomer}
      />
    </PageTemplate>
  );
};

export default Customers;
